import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { leadsAPI, communicationsAPI, usersAPI, tasksAPI, documentsAPI } from '../services/api';
import { useToast } from './Toast';
import { formatCurrency, formatPhoneDisplay, getStatusColor, formatStatusLabel } from '../utils/formatters';
import CommunicationTimeline from './CommunicationTimeline';
import DealWonModal from './DealWonModal';
import DealLostModal from './DealLostModal';
import {
  ArrowLeft, Phone, MessageCircle, X,
  AlertCircle, Plus, Pencil as PencilIcon,
  Calendar, CheckCircle, Upload, Download, Trash2, CalendarDays,
  MapPin, DollarSign, Target, Globe,
  Flame, Thermometer, Snowflake, Skull,
  Clock, PhoneCall, Mail,
} from 'lucide-react';

const INTENT_OPTIONS = ['Buy', 'Sell', 'Rent', 'Invest'];

function formatFollowUpDisplay(value) {
  if (!value) return 'Not set';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? 'Not set' : d.toLocaleDateString('en-GB');
}

function toDateInputValue(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function formatLastActivity(dateStr) {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr).getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor(diff / (1000 * 60));
  if (hours < 1) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function getLeadHealth(lead) {
  if (!lead?.last_activity_date) {
    return { label: 'Never contacted', Icon: null, bg: 'bg-gray-50', text: 'text-gray-500', sub: '' };
  }
  const hours = (Date.now() - new Date(lead.last_activity_date).getTime()) / (1000 * 60 * 60);
  if (hours < 48) return { label: 'Hot', Icon: Flame, bg: 'bg-amber-50', text: 'text-amber-700', sub: formatLastActivity(lead.last_activity_date) };
  if (hours < 168) return { label: 'Warm', Icon: Thermometer, bg: 'bg-yellow-50', text: 'text-yellow-700', sub: formatLastActivity(lead.last_activity_date) };
  if (hours < 720) return { label: 'Cold', Icon: Snowflake, bg: 'bg-blue-50', text: 'text-blue-700', sub: formatLastActivity(lead.last_activity_date) };
  return { label: 'Dead', Icon: Skull, bg: 'bg-gray-100', text: 'text-gray-600', sub: formatLastActivity(lead.last_activity_date) };
}

const OutcomeChip = ({ label, selected, onClick }) => (
  <button
    onClick={onClick}
    className={`px-3 py-1 rounded-full text-sm border transition-all ${
      selected
        ? 'bg-blue-600 text-white border-blue-600'
        : 'border-gray-300 text-gray-700 hover:border-blue-300'
    }`}
  >
    {label}
  </button>
);

const LeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { showToast } = useToast();
  const currentUser = JSON.parse(localStorage.getItem('user') || 'null');

  const returnTo = location.state?.returnTo;
  const returnLeadState = location.state?.returnLeadState || null;
  const returnScroll = location.state?.returnScroll ?? 0;
  const handleBack = () => {
    if (returnTo) {
      navigate(returnTo, { state: { restoreScroll: returnScroll, restoreLeadState: returnLeadState } });
    } else if (window.history.length > 1) {
      navigate(-1);
    } else {
      navigate('/leads');
    }
  };

  const [lead, setLead] = useState(null);
  const [leadStatus, setLeadStatus] = useState('');
  const [communications, setCommunications] = useState([]);
  const [commCount, setCommCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingStatus, setSavingStatus] = useState(false);
  const [activeTab, setActiveTab] = useState('communications');
  const [mobileTab, setMobileTab] = useState('timeline');
  const [dealWonModalOpen, setDealWonModalOpen] = useState(false);
  const [dealLostModalOpen, setDealLostModalOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);

  const [openPanel, setOpenPanel] = useState(null);
  const [panelStep, setPanelStep] = useState(1);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerActive, setTimerActive] = useState(false);
  const timerIntervalRef = useRef(null);

  const [phoneCallForm, setPhoneCallForm] = useState({ outcome: '', notes: '' });
  const [whatsappMsgForm, setWhatsappMsgForm] = useState({ outcome: '', notes: '' });
  const [whatsappCallForm, setWhatsappCallForm] = useState({ outcome: '', notes: '' });

  const [editingField, setEditingField] = useState(null);
  const [fieldValues, setFieldValues] = useState({
    source: '',
    intent: '',
    budget: '',
    interested_in: '',
    assigned_to: '',
    follow_up_date: '',
    notes: ''
  });
  const [users, setUsers] = useState([]);

  const [followups, setFollowups] = useState([]);
  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({ type: 'Call', due_date: '', notes: '' });
  const [scheduling, setScheduling] = useState(false);

  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadError, setUploadError] = useState('');

  const [meetingForm, setMeetingForm] = useState({ date: '', duration: '', notes: '', outcome: '' });

  // Tags state
  const [tags, setTags] = useState([]);
  const [newTag, setNewTag] = useState('');
  const [showTagInput, setShowTagInput] = useState(false);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const usersRes = await usersAPI.getAll();
        setUsers(usersRes.data || []);
      } catch (error) {
        console.error('Failed to load users:', error);
      }
    };
    loadUsers();
  }, []);

  useEffect(() => {
    if (!lead) return;
    if (editingField === 'all') return;
    setFieldValues({
      source: lead.source || '',
      intent: lead.intent || '',
      budget: lead.budget != null && lead.budget !== '' ? String(lead.budget) : '',
      interested_in: lead.property_type || '',
      assigned_to: lead.assigned_to_id != null && lead.assigned_to_id !== '' ? String(lead.assigned_to_id) : '',
      follow_up_date: toDateInputValue(lead.follow_up_date),
      notes: lead.notes || ''
    });
  }, [lead, editingField]);

  useEffect(() => {
    loadLeadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (activeTab === 'documents' && id) {
      loadDocuments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, id]);

  const loadLeadData = async () => {
    try {
      setLoading(true);
      const leadRes = await leadsAPI.getById(id);
      setLead(leadRes.data);
      setLeadStatus(leadRes.data?.status || 'new');
      const tagList = leadRes.data?.tags
        ? leadRes.data.tags.split(',').map(t => t.trim()).filter(Boolean)
        : [];
      setTags(tagList);

      const [commsRes, countRes, tasksRes] = await Promise.all([
        communicationsAPI.getMessages(id).catch(() => ({ data: [] })),
        communicationsAPI.getCount(id).catch(() => ({ data: null })),
        tasksAPI.getAll({ lead_id: id }).catch(() => ({ data: [] }))
      ]);

      setCommunications(commsRes.data || []);
      setCommCount(countRes.data);
      setFollowups(tasksRes.data || []);
    } catch (error) {
      console.error('Failed to load lead data:', error);
      showToast(error.response?.data?.error || 'Failed to load lead', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadDocuments = async () => {
    setDocumentsLoading(true);
    try {
      const res = await documentsAPI.getByLead(id);
      setDocuments(res.data || []);
    } catch (error) {
      console.error('Failed to load documents:', error);
    } finally {
      setDocumentsLoading(false);
    }
  };

  useEffect(() => {
    if (timerActive) {
      timerIntervalRef.current = setInterval(() => {
        setTimerSeconds(s => s + 1);
      }, 1000);
    } else {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    }
    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
    };
  }, [timerActive]);

  const startTimer = () => {
    setTimerSeconds(0);
    setTimerActive(true);
  };

  const stopTimer = () => {
    setTimerActive(false);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const handleOpenPanel = (panelName) => {
    setOpenPanel(panelName);
    setPanelStep(1);
    setTimerSeconds(0);
    setTimerActive(false);
    setPhoneCallForm({ outcome: '', notes: '' });
    setWhatsappMsgForm({ outcome: '', notes: '' });
    setWhatsappCallForm({ outcome: '', notes: '' });
  };

  const handleClosePanel = () => {
    setOpenPanel(null);
    setPanelStep(1);
    setTimerActive(false);
  };

  const handlePhoneCallNow = () => {
    const phone = lead?.phone || lead?.contact_number;
    if (phone) {
      window.open(`tel:${String(phone).replace(/\s/g, '')}`);
      startTimer();
    }
  };

  const handlePhoneCallEnd = () => {
    stopTimer();
    setPanelStep(2);
  };

  const handlePhoneCallSave = async () => {
    try {
      await communicationsAPI.logCommunication(id, {
        type: 'phone_call',
        direction: 'outbound',
        duration_seconds: timerSeconds,
        outcome: phoneCallForm.outcome || '',
        notes: phoneCallForm.notes || ''
      });
      showToast('Phone call logged successfully', 'success');
      handleClosePanel();
      loadLeadData();
    } catch (error) {
      console.error('Failed to log phone call:', error);
      showToast(error.response?.data?.error || 'Failed to log call', 'error');
    }
  };

  const handleWhatsAppMsgOpen = () => {
    const rawPhone = lead?.phone || lead?.contact_number;
    if (rawPhone) {
      const digits = String(rawPhone).replace(/\D/g, '');
      const num = digits.startsWith('0') ? '92' + digits.slice(1) : digits;
      window.open(`https://wa.me/${num}`, '_blank');
    }
  };

  const handleWhatsAppMsgSave = async () => {
    try {
      await communicationsAPI.logCommunication(id, {
        type: 'whatsapp_message',
        direction: 'outbound',
        outcome: whatsappMsgForm.outcome || '',
        notes: whatsappMsgForm.notes || ''
      });
      showToast('WhatsApp message logged successfully', 'success');
      handleClosePanel();
      loadLeadData();
    } catch (error) {
      console.error('Failed to log message:', error);
      showToast(error.response?.data?.error || 'Failed to log message', 'error');
    }
  };

  const handleWhatsAppCallOpen = () => {
    const rawPhone = lead?.phone || lead?.contact_number;
    if (rawPhone) {
      const digits = String(rawPhone).replace(/\D/g, '');
      const num = digits.startsWith('0') ? '92' + digits.slice(1) : digits;
      window.open(`https://wa.me/${num}`, '_blank');
      startTimer();
    }
  };

  const handleWhatsAppCallEnd = () => {
    stopTimer();
    setPanelStep(2);
  };

  const handleWhatsAppCallSave = async () => {
    try {
      await communicationsAPI.logCommunication(id, {
        type: 'whatsapp_call',
        direction: 'outbound',
        duration_seconds: timerSeconds,
        outcome: whatsappCallForm.outcome || '',
        notes: whatsappCallForm.notes || ''
      });
      showToast('WhatsApp call logged successfully', 'success');
      handleClosePanel();
      loadLeadData();
    } catch (error) {
      console.error('Failed to log call:', error);
      showToast(error.response?.data?.error || 'Failed to log call', 'error');
    }
  };

  const getStatusBadgeLabel = () => formatStatusLabel(leadStatus);
  const getStatusBadgeClass = () => getStatusColor(leadStatus);

  const mapFieldKeyForApi = (field) => {
    if (field === 'interested_in') return 'property_type';
    if (field === 'assigned_to') return 'assigned_to_id';
    return field;
  };

  const saveField = async (field) => {
    try {
      const key = mapFieldKeyForApi(field);
      let value = fieldValues[field];
      if (field === 'assigned_to') value = value === '' ? null : Number(value) || null;
      if (field === 'follow_up_date') value = value === '' ? null : value;
      if (field === 'budget') value = value === '' ? null : Number(value) || null;
      const payload = { [key]: value };
      await leadsAPI.update(lead.id, payload);
      setEditingField(null);
      showToast('Updated ✓', 'success');
      loadLeadData();
    } catch (error) {
      console.error('Failed to save field:', error);
      showToast(error.response?.data?.error || 'Failed to save', 'error');
    }
  };

  const saveNotes = async () => {
    try {
      await leadsAPI.update(lead.id, { notes: fieldValues.notes });
      setEditingField(null);
      showToast('Notes saved ✓', 'success');
      loadLeadData();
    } catch (error) {
      console.error('Failed to save notes:', error);
      showToast(error.response?.data?.error || 'Failed to save notes', 'error');
    }
  };

  const saveLeadStatus = async (newStatus) => {
    if (newStatus === 'won') {
      setPendingStatus('won');
      setDealWonModalOpen(true);
      return;
    }
    if (newStatus === 'lost') {
      setPendingStatus('lost');
      setDealLostModalOpen(true);
      return;
    }

    setSavingStatus(true);
    const prev = leadStatus;
    setLeadStatus(newStatus);
    try {
      await leadsAPI.update(lead.id, { status: newStatus });
      showToast('Status updated ✓', 'success');
      loadLeadData();
    } catch (error) {
      setLeadStatus(prev);
      console.error('Failed to update status:', error);
      showToast(error.response?.data?.error || 'Failed to update status', 'error');
    } finally {
      setSavingStatus(false);
    }
  };

  const handleDealWonConfirm = async (dealData) => {
    setDealWonModalOpen(false);
    setSavingStatus(true);
    setLeadStatus('won');
    try {
      await leadsAPI.update(lead.id, { status: 'won', ...dealData });
      showToast('Deal won ✓', 'success');
      loadLeadData();
    } catch (error) {
      setLeadStatus(pendingStatus === 'won' ? leadStatus : leadStatus);
      console.error('Failed to save deal:', error);
      showToast(error.response?.data?.error || 'Failed to save deal', 'error');
    } finally {
      setSavingStatus(false);
      setPendingStatus(null);
    }
  };

  const handleDealLostConfirm = async (lostData) => {
    setDealLostModalOpen(false);
    setSavingStatus(true);
    setLeadStatus('lost');
    try {
      await leadsAPI.update(lead.id, { status: 'lost', ...lostData });
      showToast('Status updated ✓', 'success');
      loadLeadData();
    } catch (error) {
      setLeadStatus(pendingStatus === 'lost' ? leadStatus : leadStatus);
      console.error('Failed to update status:', error);
      showToast(error.response?.data?.error || 'Failed to update status', 'error');
    } finally {
      setSavingStatus(false);
      setPendingStatus(null);
    }
  };

  const handleDealWonClose = () => {
    setDealWonModalOpen(false);
    setPendingStatus(null);
    setLeadStatus(leadStatus);
  };

  const handleDealLostClose = () => {
    setDealLostModalOpen(false);
    setPendingStatus(null);
    setLeadStatus(leadStatus);
  };

  const saveAll = async () => {
    try {
      const payload = {
        source: fieldValues.source,
        intent: fieldValues.intent,
        budget: fieldValues.budget,
        property_type: fieldValues.interested_in,
        assigned_to_id: fieldValues.assigned_to === '' ? null : fieldValues.assigned_to,
        follow_up_date: fieldValues.follow_up_date === '' ? null : fieldValues.follow_up_date,
        notes: fieldValues.notes
      };
      await leadsAPI.update(lead.id, payload);
      setEditingField(null);
      showToast('Lead updated', 'success');
      loadLeadData();
    } catch (error) {
      console.error('Failed to save lead:', error);
      showToast('Failed to save', 'error');
    }
  };

  const cancelEditAll = () => {
    setFieldValues({
      source: lead.source || '',
      intent: lead.intent || '',
      budget: lead.budget != null && lead.budget !== '' ? String(lead.budget) : '',
      interested_in: lead.property_type || '',
      assigned_to: lead.assigned_to_id != null && lead.assigned_to_id !== '' ? String(lead.assigned_to_id) : '',
      follow_up_date: toDateInputValue(lead.follow_up_date),
      notes: lead.notes || ''
    });
    setEditingField(null);
  };

  const getInitials = (name) => {
    if (!name) return 'N/A';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const getDaysActive = () => {
    if (!lead?.created_at) return 0;
    const created = new Date(lead.created_at);
    const now = new Date();
    return Math.floor((now - created) / (1000 * 60 * 60 * 24));
  };

  const phone = lead?.phone || lead?.contact_number;
  const lastFiveCommunications = communications.slice(0, 5);
  const isEditingAll = editingField === 'all';
  const editingKey = (key) => isEditingAll || editingField === key;
  const startSingleEdit = (key) => {
    if (editingField !== 'all') setEditingField(key);
  };
  const assignedDisplayLabel = () => {
    const idVal = fieldValues.assigned_to;
    if (idVal === '' || idVal == null) return 'Unassigned';
    const u = users.find(x => String(x.id) === String(idVal));
    return u?.full_name || u?.username || lead.assigned_to_name || lead.assigned_to || 'Unassigned';
  };
  const budgetDisplay = () => {
    const v = fieldValues.budget;
    if (v === '' || v == null) return 'Not specified';
    return formatCurrency(v);
  };
  const healthInfo = getLeadHealth(lead);

  const handleAddTag = async () => {
    const trimmed = newTag.trim();
    if (!trimmed) return;
    const updated = [...tags, trimmed];
    setTags(updated);
    setNewTag('');
    setShowTagInput(false);
    try {
      await leadsAPI.updateTags(lead.id, updated.join(','));
      showToast('Tag added', 'success');
    } catch (err) {
      console.error('Failed to add tag:', err);
      showToast('Failed to add tag', 'error');
      loadLeadData();
    }
  };

  const handleRemoveTag = async (tagToRemove) => {
    const updated = tags.filter(t => t !== tagToRemove);
    setTags(updated);
    try {
      await leadsAPI.updateTags(lead.id, updated.join(','));
      showToast('Tag removed', 'success');
    } catch (err) {
      console.error('Failed to remove tag:', err);
      showToast('Failed to remove tag', 'error');
      loadLeadData();
    }
  };

  if (loading) {
    return (
      <div className="h-[calc(100vh-56px)] bg-[#f5f4f2] flex items-center justify-center">
        <div className="bg-white rounded-xl border border-gray-200 p-6 w-full max-w-4xl mx-4">
          <div className="h-8 w-72 bg-gray-200 rounded animate-pulse mb-4" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => <div key={i} className="h-24 bg-gray-200 rounded animate-pulse" />)}
          </div>
        </div>
      </div>
    );
  }

  if (!lead) {
    return (
      <div className="h-[calc(100vh-56px)] bg-[#f5f4f2] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Lead not found</h2>
          <button onClick={handleBack} className="text-blue-600 hover:underline">
            Go back to leads
          </button>
        </div>
      </div>
    );
  }

  const leadName = lead.name || lead.purchaser_name || lead.customer_name;

  return (
    <div className="min-h-[calc(100vh-56px)] flex flex-col bg-[#f5f4f2]">
      {/* ===== MOBILE TAB SWITCHER ===== */}
      <div className="lg:hidden flex-shrink-0 bg-white border-b border-gray-200 flex">
        {[
          { key: 'info', label: 'Info' },
          { key: 'timeline', label: 'Timeline' },
          { key: 'controls', label: 'Controls' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setMobileTab(tab.key)}
            className={`flex-1 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              mobileTab === tab.key
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ===== PAGE CONTENT ===== */}
      <div className="flex-1">
        <div className="p-4 lg:p-6 lg:px-7">
          <button onClick={handleBack} className="flex items-center gap-1.5 text-[13px] text-gray-500 hover:text-gray-900 cursor-pointer mb-4" style={{ padding: 0, background: 'transparent', border: 'none', textDecoration: 'none' }}>
            <ArrowLeft size={14} />
            Back to leads
          </button>
          <div className="flex gap-5">
        {/* ===== LEFT PANEL — 200px ===== */}
        <div className={`${mobileTab === 'info' ? 'flex' : 'hidden'} lg:flex w-full lg:w-[240px] flex-shrink-0 flex-col bg-white border border-gray-200 rounded-xl`}>
          {/* Profile card */}
          <div className="px-4 pt-5 lg:pt-6 pb-[18px] border-b border-gray-100">
            <div className="flex flex-col items-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center text-lg font-bold text-white`} style={{ backgroundColor: `hsl(${leadName ? leadName.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % 360 : 200}, 55%, 50%)` }}>
                {getInitials(leadName)}
              </div>
              <h2 className="mt-3 font-semibold text-base text-gray-900 truncate max-w-full text-center">{leadName}</h2>
              <p className="mt-0.5 text-xs text-gray-500 text-center">
                Lead #{lead.id} · {lead.created_at ? new Date(lead.created_at).toLocaleDateString('en-GB', { month: 'short', day: 'numeric', year: 'numeric' }) : ''}
              </p>
              <span className={`mt-3 inline-flex items-center px-[10px] py-[2px] rounded-full text-[11px] font-medium ${getStatusBadgeClass()}`}>
                {getStatusBadgeLabel()}
              </span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex justify-around px-[18px] py-[14px] border-b border-gray-100">
            <button onClick={() => handleOpenPanel('phone')} className="flex flex-col items-center group" title="Phone Call">
              <div className="w-8 h-8 rounded-full flex items-center justify-center border border-gray-200 bg-white group-hover:bg-[#E6F1FB] group-hover:border-[#85B7EB] transition-colors">
                <Phone size={16} className="text-gray-400 group-hover:text-[#185FA5] transition-colors" />
              </div>
              <span className="text-[10px] text-gray-400 mt-1">Call</span>
            </button>
            <button onClick={() => handleOpenPanel('whatsapp_msg')} className="flex flex-col items-center group" title="WhatsApp">
              <div className="w-8 h-8 rounded-full flex items-center justify-center border border-gray-200 bg-white group-hover:bg-[#E6F1FB] group-hover:border-[#85B7EB] transition-colors">
                <MessageCircle size={16} className="text-gray-400 group-hover:text-[#185FA5] transition-colors" />
              </div>
              <span className="text-[10px] text-gray-400 mt-1">WhatsApp</span>
            </button>
            <button onClick={() => handleOpenPanel('whatsapp_call')} className="flex flex-col items-center group" title="WA Call">
              <div className="w-8 h-8 rounded-full flex items-center justify-center border border-gray-200 bg-white group-hover:bg-[#E6F1FB] group-hover:border-[#85B7EB] transition-colors">
                <PhoneCall size={16} className="text-gray-400 group-hover:text-[#185FA5] transition-colors" />
              </div>
              <span className="text-[10px] text-gray-400 mt-1">WA Call</span>
            </button>
            <button onClick={() => handleOpenPanel('meeting')} className="flex flex-col items-center group" title="Log Meeting">
              <div className="w-8 h-8 rounded-full flex items-center justify-center border border-gray-200 bg-white group-hover:bg-[#E6F1FB] group-hover:border-[#85B7EB] transition-colors">
                <CalendarDays size={16} className="text-gray-400 group-hover:text-[#185FA5] transition-colors" />
              </div>
              <span className="text-[10px] text-gray-400 mt-1">Meeting</span>
            </button>
          </div>

          {/* Contact details */}
          <div className="px-4 py-4 border-b border-gray-100">
            <div className="space-y-[13px]">
              {[
                { icon: Phone, label: 'Phone', value: phone, href: phone ? `tel:${String(phone).replace(/\s/g, '')}` : null, display: formatPhoneDisplay(phone) },
                { icon: Mail, label: 'Email', value: lead.email, href: lead.email ? `mailto:${lead.email}` : null, display: lead.email || '—' },
                { icon: MapPin, label: 'Location', value: lead.location, display: lead.location || '—' },
                { icon: DollarSign, label: 'Budget', value: lead.budget, display: lead.budget ? formatCurrency(lead.budget) : '—' },
                { icon: Target, label: 'Intent', value: lead.intent, display: lead.intent || '—' },
                { icon: Globe, label: 'Source', value: lead.source, display: lead.source || '—' },
              ].map((field, idx) => (
                <div key={idx}>
                  <div className="flex items-center gap-1.5 text-[10px] font-medium text-gray-400 uppercase tracking-[0.3px]">
                    <field.icon size={11} className="text-gray-400" />
                    {field.label}
                  </div>
                  {field.href ? (
                    <a href={field.href} className="text-[12px] text-[#185FA5] hover:underline truncate block mt-[2px] pl-[1px]">{field.display}</a>
                  ) : (
                    <span className="text-[12px] text-gray-700 truncate block mt-[2px] pl-[1px]">{field.display}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Tags */}
          <div className="px-4 pt-[13px]">
            <h3 className="text-[10px] font-medium text-gray-400 uppercase tracking-[0.3px] mb-2">Tags</h3>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {tags.map((tag, i) => (
                <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                  {tag}
                  <button onClick={() => handleRemoveTag(tag)} className="text-gray-400 hover:text-gray-600">
                    <X size={10} />
                  </button>
                </span>
              ))}
            </div>
            <button onClick={() => setShowTagInput(true)} className="inline-flex items-center border border-dashed border-gray-300 rounded-full px-[10px] py-[2px] text-[11px] text-gray-500 hover:text-blue-600 hover:border-blue-300 transition-colors bg-transparent cursor-pointer">
              <Plus size={12} className="mr-1" />
              Add tag
            </button>
          </div>

          {/* Lost Reason */}
          {lead.status === 'lost' && lead.lost_reason && (
            <div className="px-4 pt-4 mt-4 border-t border-gray-100 pb-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <h3 className="text-[10px] font-semibold text-red-500 uppercase tracking-wide mb-1">LOST REASON</h3>
                <p className="text-sm text-red-700">{lead.lost_reason}</p>
              </div>
            </div>
          )}
        </div>

        {/* ===== CENTER PANEL — flex:1 ===== */}
        <div className={`${mobileTab === 'timeline' ? 'flex' : 'hidden'} lg:flex flex-1 min-w-0 flex-col bg-white border border-gray-200 rounded-xl`}>
          {/* Inline Panels */}
          {openPanel && (
            <div className="bg-white border-b border-gray-200 px-4 py-4">
              {openPanel === 'phone' && (
                <div className="bg-white rounded-xl border border-gray-200 p-4 max-w-md">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold text-gray-900">Phone Call</h3>
                    <button onClick={handleClosePanel} className="text-gray-400 hover:text-gray-600">
                      <X size={18} />
                    </button>
                  </div>
                  {panelStep === 1 ? (
                    <>
                      <div className="mb-4">
                        <p className="text-sm text-gray-600 mb-1">{leadName}</p>
                        <p className="text-lg font-semibold text-gray-900">{formatPhoneDisplay(phone)}</p>
                      </div>
                      <div className="text-center mb-6">
                        <div className="font-mono text-4xl font-bold text-gray-900">{formatTime(timerSeconds)}</div>
                      </div>
                      <div className="space-y-2">
                        <button onClick={handlePhoneCallNow} className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm">Call now (opens dialler)</button>
                        <button onClick={handlePhoneCallEnd} disabled={timerSeconds === 0} className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm disabled:opacity-50">End — log notes</button>
                        <button onClick={handleClosePanel} className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm">Cancel</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="mb-4"><p className="text-sm text-gray-500">Duration: {Math.floor(timerSeconds / 60)} min {timerSeconds % 60} sec</p></div>
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-900 mb-2">Outcome</label>
                        <div className="flex flex-wrap gap-2">
                          {['Answered', 'No answer', 'Busy', 'Voicemail'].map(outcome => (
                            <OutcomeChip key={outcome} label={outcome} selected={phoneCallForm.outcome === outcome} onClick={() => setPhoneCallForm({ ...phoneCallForm, outcome })} />
                          ))}
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-900 mb-2">Notes (optional)</label>
                        <textarea value={phoneCallForm.notes} onChange={e => setPhoneCallForm({ ...phoneCallForm, notes: e.target.value })} placeholder="What was discussed?" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm" rows={2} />
                      </div>
                      <div className="space-y-2">
                        <button onClick={handlePhoneCallSave} className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm">Save & close</button>
                        <button onClick={handleClosePanel} className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm">Skip — no notes</button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {openPanel === 'whatsapp_msg' && (
                <div className="bg-white rounded-xl border border-gray-200 p-4 max-w-md">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold text-gray-900">WhatsApp Message</h3>
                    <button onClick={handleClosePanel} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
                  </div>
                  <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex gap-2">
                    <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">This opens WhatsApp on your phone. After sending, come back here to log the outcome.</p>
                  </div>
                  <button onClick={handleWhatsAppMsgOpen} className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm mb-4">Open WhatsApp chat</button>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3">Log this interaction</h4>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-900 mb-2">Outcome</label>
                    <div className="flex flex-wrap gap-2">
                      {['Sent & replied', 'Sent, no reply', 'Not delivered'].map(outcome => (
                        <OutcomeChip key={outcome} label={outcome} selected={whatsappMsgForm.outcome === outcome} onClick={() => setWhatsappMsgForm({ ...whatsappMsgForm, outcome })} />
                      ))}
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-900 mb-2">Notes (optional)</label>
                    <textarea value={whatsappMsgForm.notes} onChange={e => setWhatsappMsgForm({ ...whatsappMsgForm, notes: e.target.value })} placeholder="What was the message about?" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm" rows={2} />
                  </div>
                  <div className="space-y-2">
                    <button onClick={handleWhatsAppMsgSave} className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm">Save log</button>
                    <button onClick={handleClosePanel} className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm">Cancel — don't log</button>
                  </div>
                </div>
              )}

              {openPanel === 'whatsapp_call' && (
                <div className="bg-white rounded-xl border border-gray-200 p-4 max-w-md">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold text-gray-900">WhatsApp Call</h3>
                    <button onClick={handleClosePanel} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
                  </div>
                  {panelStep === 1 ? (
                    <>
                      <div className="mb-4">
                        <p className="text-sm text-gray-600 mb-1">{leadName}</p>
                        <p className="text-lg font-semibold text-gray-900">{formatPhoneDisplay(phone)}</p>
                      </div>
                      <div className="text-center mb-6">
                        <div className="font-mono text-4xl font-bold text-gray-900">{formatTime(timerSeconds)}</div>
                      </div>
                      <div className="space-y-2">
                        <button onClick={handleWhatsAppCallOpen} className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium text-sm">Open in WhatsApp</button>
                        <button onClick={handleWhatsAppCallEnd} disabled={timerSeconds === 0} className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm disabled:opacity-50">End — log notes</button>
                        <button onClick={handleClosePanel} className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm">Cancel</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="mb-4"><p className="text-sm text-gray-500">Duration: {Math.floor(timerSeconds / 60)} min {timerSeconds % 60} sec</p></div>
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-900 mb-2">Outcome</label>
                        <div className="flex flex-wrap gap-2">
                          {['Connected', 'No answer', 'Busy'].map(outcome => (
                            <OutcomeChip key={outcome} label={outcome} selected={whatsappCallForm.outcome === outcome} onClick={() => setWhatsappCallForm({ ...whatsappCallForm, outcome })} />
                          ))}
                        </div>
                      </div>
                      <div className="mb-3">
                        <label className="block text-sm font-medium text-gray-900 mb-2">Notes (optional)</label>
                        <textarea value={whatsappCallForm.notes} onChange={e => setWhatsappCallForm({ ...whatsappCallForm, notes: e.target.value })} placeholder="What did you discuss?" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm" rows={2} />
                      </div>
                      <div className="space-y-2">
                        <button onClick={handleWhatsAppCallSave} className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm">Save & close</button>
                        <button onClick={handleClosePanel} className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm">Skip — no notes</button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {openPanel === 'meeting' && (
                <div className="bg-white rounded-xl border border-gray-200 p-4 max-w-md">
                  <div className="flex justify-between items-center mb-3">
                    <h3 className="font-semibold text-gray-900">Log Meeting</h3>
                    <button onClick={handleClosePanel} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-900 mb-1">Date</label>
                    <input type="date" value={meetingForm.date} onChange={e => setMeetingForm({ ...meetingForm, date: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm" />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-900 mb-1">Duration (minutes)</label>
                    <input type="number" min="1" value={meetingForm.duration} onChange={e => setMeetingForm({ ...meetingForm, duration: e.target.value })} placeholder="e.g. 30" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm" />
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-900 mb-2">Outcome</label>
                    <div className="flex flex-wrap gap-2">
                      {['Completed', 'Partially completed', 'Rescheduled', 'Cancelled'].map(outcome => (
                        <OutcomeChip key={outcome} label={outcome} selected={meetingForm.outcome === outcome} onClick={() => setMeetingForm({ ...meetingForm, outcome })} />
                      ))}
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-900 mb-1">Notes</label>
                    <textarea value={meetingForm.notes} onChange={e => setMeetingForm({ ...meetingForm, notes: e.target.value })} placeholder="Meeting notes, agenda, decisions..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm" rows={2} />
                  </div>
                  <div className="space-y-2">
                    <button onClick={async () => {
                      if (!meetingForm.date) return showToast('Please select a date', 'error');
                      try {
                        const durationMinutes = parseInt(meetingForm.duration) || 0;
                        await communicationsAPI.logCommunication(id, {
                          type: 'meeting',
                          direction: 'inbound',
                          duration_seconds: durationMinutes * 60,
                          outcome: meetingForm.outcome || '',
                          notes: meetingForm.notes || '',
                          sent_at: new Date(meetingForm.date).toISOString()
                        });
                        showToast('Meeting logged successfully', 'success');
                        handleClosePanel();
                        setMeetingForm({ date: '', duration: '', notes: '', outcome: '' });
                        loadLeadData();
                      } catch (error) {
                        console.error('Failed to log meeting:', error);
                        showToast(error.response?.data?.error || 'Failed to log meeting', 'error');
                      }
                    }} className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm">Save meeting</button>
                    <button onClick={handleClosePanel} className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium text-sm">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Tabs */}
          <div className="flex-shrink-0 bg-white border-b border-gray-200 px-4 pt-3">
            <div className="flex gap-5">
              {[
                { name: 'Overview', value: 'overview' },
                { name: 'Communications', value: 'communications', count: commCount?.total },
                { name: 'Documents', value: 'documents', count: documents.length || 0 }
              ].map(tab => (
                <button
                  key={tab.value}
                  onClick={() => setActiveTab(tab.value)}
                  className={`flex items-center pb-2 pt-2 text-sm transition-colors border-b-2 ${
                    activeTab === tab.value
                      ? 'border-blue-600 text-blue-600 font-semibold'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab.name}
                  {tab.count !== undefined && tab.count !== null && (
                    <span className="ml-1.5 bg-gray-100 text-gray-500 rounded-full px-1.5 py-0.5 text-xs font-medium">
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 px-4 py-4">
            {activeTab === 'overview' && (
              <div className="space-y-5">
                {/* LEAD DETAILS */}
                <div className="bg-white border border-gray-200 rounded-lg p-[18px] lg:p-5">
                  <h3 className="text-[10px] font-medium text-gray-400 uppercase tracking-[0.5px] pb-3">LEAD DETAILS</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase tracking-[0.3px] mb-[3px]">Source</p>
                      {editingKey('source') ? (
                        <div className={isEditingAll ? '' : 'flex items-center gap-2'}>
                          <input autoFocus={!isEditingAll} className={`border border-blue-400 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none ${isEditingAll ? 'w-full' : 'flex-1 min-w-0'}`} value={fieldValues.source} onChange={e => setFieldValues(p => ({ ...p, source: e.target.value }))} onKeyDown={e => { if (e.key === 'Enter') saveField('source'); if (e.key === 'Escape') setEditingField(null); }} />
                          {!isEditingAll && (<><button type="button" onClick={() => saveField('source')} className="text-xs bg-blue-600 text-white px-2 py-1 rounded shrink-0">Save</button><button type="button" onClick={() => setEditingField(null)} className="text-xs text-gray-400 hover:text-gray-600 shrink-0">✕</button></>)}
                        </div>
                      ) : (
                        <div role="presentation" onClick={() => startSingleEdit('source')} className="cursor-pointer hover:bg-blue-50 rounded px-1 -mx-1 transition-colors group">
                          <span className="text-[13px] text-gray-900">{fieldValues.source || 'Manual'}</span>
                          <PencilIcon className="w-3 h-3 text-gray-300 group-hover:text-blue-400 inline ml-1 opacity-0 group-hover:opacity-100 align-middle" />
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase tracking-[0.3px] mb-[3px]">Intent</p>
                      {editingKey('intent') ? (
                        <div className={isEditingAll ? '' : 'flex items-center gap-2'}>
                          <select autoFocus={!isEditingAll} className={`border border-blue-400 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white ${isEditingAll ? 'w-full' : 'flex-1 min-w-0'}`} value={fieldValues.intent} onChange={e => setFieldValues(p => ({ ...p, intent: e.target.value }))} onKeyDown={e => { if (e.key === 'Escape') setEditingField(null); }}>
                            <option value="">Not specified</option>
                            {fieldValues.intent && !INTENT_OPTIONS.includes(fieldValues.intent) && <option value={fieldValues.intent}>{fieldValues.intent}</option>}
                            {INTENT_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                          </select>
                          {!isEditingAll && (<><button type="button" onClick={() => saveField('intent')} className="text-xs bg-blue-600 text-white px-2 py-1 rounded shrink-0">Save</button><button type="button" onClick={() => setEditingField(null)} className="text-xs text-gray-400 hover:text-gray-600 shrink-0">✕</button></>)}
                        </div>
                      ) : (
                        <div role="presentation" onClick={() => startSingleEdit('intent')} className="cursor-pointer hover:bg-blue-50 rounded px-1 -mx-1 transition-colors group">
                          <span className="text-[13px] text-gray-900">{fieldValues.intent || 'Not specified'}</span>
                          <PencilIcon className="w-3 h-3 text-gray-300 group-hover:text-blue-400 inline ml-1 opacity-0 group-hover:opacity-100 align-middle" />
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase tracking-[0.3px] mb-[3px]">Budget</p>
                      {editingKey('budget') ? (
                        <div className={isEditingAll ? '' : 'flex items-center gap-2'}>
                          <input autoFocus={!isEditingAll} className={`border border-blue-400 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none ${isEditingAll ? 'w-full' : 'flex-1 min-w-0'}`} value={fieldValues.budget} onChange={e => setFieldValues(p => ({ ...p, budget: e.target.value }))} onKeyDown={e => { if (e.key === 'Enter') saveField('budget'); if (e.key === 'Escape') setEditingField(null); }} />
                          {!isEditingAll && (<><button type="button" onClick={() => saveField('budget')} className="text-xs bg-blue-600 text-white px-2 py-1 rounded shrink-0">Save</button><button type="button" onClick={() => setEditingField(null)} className="text-xs text-gray-400 hover:text-gray-600 shrink-0">✕</button></>)}
                        </div>
                      ) : (
                        <div role="presentation" onClick={() => startSingleEdit('budget')} className="cursor-pointer hover:bg-blue-50 rounded px-1 -mx-1 transition-colors group">
                          <span className="text-[13px] text-gray-900">{budgetDisplay()}</span>
                          <PencilIcon className="w-3 h-3 text-gray-300 group-hover:text-blue-400 inline ml-1 opacity-0 group-hover:opacity-100 align-middle" />
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase tracking-[0.3px] mb-[3px]">Interested in</p>
                      {editingKey('interested_in') ? (
                        <div className={isEditingAll ? '' : 'flex items-center gap-2'}>
                          <input autoFocus={!isEditingAll} className={`border border-blue-400 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none ${isEditingAll ? 'w-full' : 'flex-1 min-w-0'}`} value={fieldValues.interested_in} onChange={e => setFieldValues(p => ({ ...p, interested_in: e.target.value }))} onKeyDown={e => { if (e.key === 'Enter') saveField('interested_in'); if (e.key === 'Escape') setEditingField(null); }} />
                          {!isEditingAll && (<><button type="button" onClick={() => saveField('interested_in')} className="text-xs bg-blue-600 text-white px-2 py-1 rounded shrink-0">Save</button><button type="button" onClick={() => setEditingField(null)} className="text-xs text-gray-400 hover:text-gray-600 shrink-0">✕</button></>)}
                        </div>
                      ) : (
                        <div role="presentation" onClick={() => startSingleEdit('interested_in')} className="cursor-pointer hover:bg-blue-50 rounded px-1 -mx-1 transition-colors group">
                          <span className="text-[13px] text-gray-900">{fieldValues.interested_in || 'Not specified'}</span>
                          <PencilIcon className="w-3 h-3 text-gray-300 group-hover:text-blue-400 inline ml-1 opacity-0 group-hover:opacity-100 align-middle" />
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase tracking-[0.3px] mb-[3px]">Assigned to</p>
                      {editingKey('assigned_to') ? (
                        <div className={isEditingAll ? '' : 'flex items-center gap-2'}>
                          <select autoFocus={!isEditingAll} className={`border border-blue-400 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white ${isEditingAll ? 'w-full' : 'flex-1 min-w-0'}`} value={fieldValues.assigned_to} onChange={e => setFieldValues(p => ({ ...p, assigned_to: e.target.value }))} onKeyDown={e => { if (e.key === 'Escape') setEditingField(null); }}>
                            <option value="">Unassigned</option>
                            {users.map(u => <option key={u.id} value={String(u.id)}>{u.full_name || u.username}</option>)}
                          </select>
                          {!isEditingAll && (<><button type="button" onClick={() => saveField('assigned_to')} className="text-xs bg-blue-600 text-white px-2 py-1 rounded shrink-0">Save</button><button type="button" onClick={() => setEditingField(null)} className="text-xs text-gray-400 hover:text-gray-600 shrink-0">✕</button></>)}
                        </div>
                      ) : (
                        <div role="presentation" onClick={() => startSingleEdit('assigned_to')} className="cursor-pointer hover:bg-blue-50 rounded px-1 -mx-1 transition-colors group">
                          <span className="text-[13px] text-gray-900">{assignedDisplayLabel()}</span>
                          <PencilIcon className="w-3 h-3 text-gray-300 group-hover:text-blue-400 inline ml-1 opacity-0 group-hover:opacity-100 align-middle" />
                        </div>
                      )}
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-400 uppercase tracking-[0.3px] mb-[3px]">Follow-up date</p>
                      {editingKey('follow_up_date') ? (
                        <div className={isEditingAll ? '' : 'flex items-center gap-2'}>
                          <input type="date" autoFocus={!isEditingAll} className={`border border-blue-400 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none ${isEditingAll ? 'w-full' : 'flex-1 min-w-0'}`} value={fieldValues.follow_up_date} onChange={e => setFieldValues(p => ({ ...p, follow_up_date: e.target.value }))} onKeyDown={e => { if (e.key === 'Enter') saveField('follow_up_date'); if (e.key === 'Escape') setEditingField(null); }} />
                          {!isEditingAll && (<><button type="button" onClick={() => saveField('follow_up_date')} className="text-xs bg-blue-600 text-white px-2 py-1 rounded shrink-0">Save</button><button type="button" onClick={() => setEditingField(null)} className="text-xs text-gray-400 hover:text-gray-600 shrink-0">✕</button></>)}
                        </div>
                      ) : (
                        <div role="presentation" onClick={() => startSingleEdit('follow_up_date')} className="cursor-pointer hover:bg-blue-50 rounded px-1 -mx-1 transition-colors group">
                          <span className="text-[13px] text-gray-900">{formatFollowUpDisplay(fieldValues.follow_up_date)}</span>
                          <PencilIcon className="w-3 h-3 text-gray-300 group-hover:text-blue-400 inline ml-1 opacity-0 group-hover:opacity-100 align-middle" />
                        </div>
                      )}
                    </div>
                    <div className="sm:col-span-2 mt-4">
                      <p className="text-[10px] text-gray-400 uppercase tracking-[0.3px] mb-[3px]">Notes</p>
                      {editingKey('notes') ? (
                        <div className={isEditingAll ? '' : 'flex items-start gap-2'}>
                          <textarea autoFocus={!isEditingAll} className={`border border-blue-400 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none ${isEditingAll ? 'w-full' : 'flex-1 min-w-0'}`} rows={3} value={fieldValues.notes} onChange={e => setFieldValues(p => ({ ...p, notes: e.target.value }))} />
                          {!isEditingAll && (<><button type="button" onClick={saveNotes} className="text-xs bg-blue-600 text-white px-2 py-1 rounded shrink-0">Save</button><button type="button" onClick={() => setEditingField(null)} className="text-xs text-gray-400 hover:text-gray-600 shrink-0">✕</button></>)}
                        </div>
                      ) : (
                        <div role="presentation" onClick={() => startSingleEdit('notes')} className="cursor-pointer hover:bg-blue-50 rounded px-1 -mx-1 transition-colors group">
                          <p className="text-[13px] text-gray-900 whitespace-pre-wrap">{fieldValues.notes || 'No notes'}</p>
                          <PencilIcon className="w-3 h-3 text-gray-300 group-hover:text-blue-400 inline ml-1 opacity-0 group-hover:opacity-100 align-middle" />
                        </div>
                      )}
                    </div>
                  </div>
                  {isEditingAll && (
                    <div className="flex items-center gap-2 mt-4">
                      <button type="button" onClick={saveAll} className="text-sm bg-blue-600 text-white px-4 py-2 rounded-lg font-medium">Save all</button>
                      <button type="button" onClick={cancelEditAll} className="text-sm text-gray-600 hover:text-gray-900 px-4 py-2">Cancel</button>
                    </div>
                  )}
                </div>

                {/* Communication History Preview */}
                <div className="bg-white border border-gray-200 rounded-lg p-[18px] lg:p-5">
                  <div className="flex justify-between items-center">
                    <h3 className="text-[10px] font-medium text-gray-400 uppercase tracking-[0.5px] pb-3">COMMUNICATION HISTORY</h3>
                    {communications.length > 5 && (
                      <button onClick={() => setActiveTab('communications')} className="text-xs text-blue-600 hover:text-blue-700 font-medium">View all</button>
                    )}
                  </div>
                  <CommunicationTimeline communications={lastFiveCommunications} />
                </div>
              </div>
            )}

            {activeTab === 'communications' && (
              <div className="bg-white border border-gray-200 rounded-lg p-[18px] lg:p-5">
                <CommunicationTimeline communications={communications} />
              </div>
            )}

            {activeTab === 'documents' && (
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Documents</h3>
                  <label className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 cursor-pointer">
                    <Upload className="w-4 h-4" />
                    Upload file
                    <input type="file" className="hidden" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp" disabled={uploadingFile} onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setUploadingFile(true);
                      setUploadError('');
                      try {
                        const formData = new FormData();
                        formData.append('file', file);
                        formData.append('lead_id', id);
                        await documentsAPI.upload(formData);
                        showToast('File uploaded successfully', 'success');
                        loadDocuments();
                      } catch (error) {
                        console.error('Upload failed:', error);
                        setUploadError(error.response?.data?.error || 'Failed to upload file');
                        showToast('Upload failed', 'error');
                      } finally {
                        setUploadingFile(false);
                        e.target.value = '';
                      }
                    }} />
                  </label>
                </div>
                {uploadError && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{uploadError}</div>
                )}
                {documentsLoading ? (
                  <div className="text-center py-8"><div className="animate-spin w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full mx-auto" /></div>
                ) : documents.length === 0 ? (
                  <div className="text-center py-12"><Upload className="w-10 h-10 mx-auto mb-3 text-gray-300" /><p className="text-sm text-gray-500">No documents uploaded yet</p></div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">File name</th>
                          <th className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                          <th className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Size</th>
                          <th className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Uploaded by</th>
                          <th className="text-left py-3 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="text-right py-3 px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {documents.map(doc => (
                          <tr key={doc.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="py-3 px-2">
                              <div className="flex items-center gap-2">
                                <Download className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                <span className="text-sm text-gray-900 truncate max-w-[200px]">{doc.file_name}</span>
                              </div>
                            </td>
                            <td className="py-3 px-2 text-sm text-gray-500">{doc.file_name?.split('.').pop()?.toUpperCase() || '-'}</td>
                            <td className="py-3 px-2 text-sm text-gray-500">{doc.file_size ? (doc.file_size / 1024).toFixed(1) + ' KB' : '-'}</td>
                            <td className="py-3 px-2 text-sm text-gray-500">{doc.uploaded_by_name || '-'}</td>
                            <td className="py-3 px-2 text-sm text-gray-500">{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('en-GB') : '-'}</td>
                            <td className="py-3 px-2 text-right">
                              <div className="flex items-center justify-end gap-1">
                                <button onClick={async () => {
                                  try {
                                    const res = await documentsAPI.download(doc.id);
                                    const url = window.URL.createObjectURL(new Blob([res.data]));
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = doc.file_name;
                                    a.click();
                                    window.URL.revokeObjectURL(url);
                                  } catch (error) {
                                    console.error('Download failed:', error);
                                    showToast('Failed to download', 'error');
                                  }
                                }} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded" title="Download">
                                  <Download className="w-4 h-4" />
                                </button>
                                <button onClick={async () => {
                                  if (!window.confirm('Delete this document? This cannot be undone.')) return;
                                  try {
                                    await documentsAPI.delete(doc.id);
                                    showToast('Document deleted', 'success');
                                    loadDocuments();
                                  } catch (error) {
                                    console.error('Delete failed:', error);
                                    showToast(error.response?.data?.error || 'Failed to delete', 'error');
                                  }
                                }} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete">
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ===== RIGHT PANEL — 220px ===== */}
        <div className={`${mobileTab === 'controls' ? 'flex' : 'hidden'} lg:flex w-full lg:w-[220px] flex-shrink-0 flex-col bg-white border border-gray-200 rounded-xl`}>
          {/* Edit / Delete actions */}
          <div className="px-[18px] pt-[18px] pb-4 border-b border-gray-100">
            <div className="flex gap-2" style={{ marginBottom: 0 }}>
              <button
                onClick={() => setEditingField('all')}
                className="inline-flex items-center justify-center gap-1.5 text-gray-700 text-[12px] cursor-pointer"
                style={{ flex: 1, height: '30px', borderRadius: '6px', border: '1px solid #d1d5db', background: 'transparent' }}
              >
                <PencilIcon size={13} />
                Edit
              </button>
              <button
                onClick={() => {
                  if (window.confirm('Delete this lead? This cannot be undone.')) {
                    leadsAPI.delete(lead.id)
                      .then(() => { showToast('Lead deleted', 'success'); handleBack(); })
                      .catch((err) => showToast(err.response?.data?.error || 'Failed to delete', 'error'));
                  }
                }}
                className="inline-flex items-center justify-center gap-1.5 text-[#A32D2D] text-[12px] cursor-pointer"
                style={{ flex: 1, height: '30px', borderRadius: '6px', border: '1px solid #F09595', background: 'transparent' }}
              >
                <Trash2 size={13} />
                Delete
              </button>
            </div>
          </div>
          {/* Lead Status */}
          <div className="px-[18px] py-4 border-b border-gray-100">
            <h3 className="text-[10px] font-medium text-gray-400 tracking-[0.3px] mb-2">Lead status</h3>
            <select
              value={leadStatus}
              onChange={e => saveLeadStatus(e.target.value)}
              disabled={savingStatus}
              className="w-full text-[12px] px-[10px] py-[7px] border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white disabled:opacity-60 disabled:cursor-not-allowed"
            >
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="in_progress">In Progress</option>
              <option value="follow_up">Follow Up</option>
              <option value="negotiating">Negotiating</option>
              <option value="won">Won</option>
              <option value="lost">Lost</option>
              <option value="queued_unassigned">Queued Unassigned</option>
              <option value="archived">Archived</option>
            </select>
            {savingStatus && <p className="text-xs text-gray-400 mt-1">Saving...</p>}
          </div>

          {/* Assigned To */}
          <div className="px-[18px] py-4 border-b border-gray-100">
            <h3 className="text-[10px] font-medium text-gray-400 tracking-[0.3px] mb-2">Assigned to</h3>
            <div className="flex items-center gap-2">
              <div className="w-[26px] h-[26px] rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0" style={{ backgroundColor: '#E6F1FB', color: '#185FA5' }}>
                {assignedDisplayLabel() === 'Unassigned' ? '?' : getInitials(assignedDisplayLabel())}
              </div>
              <span className="flex-1 text-[12px] text-gray-900 truncate">{assignedDisplayLabel()}</span>
            </div>
          </div>

          {/* Follow-ups */}
          <div className="px-[18px] py-4 border-b border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[10px] font-medium text-gray-400 tracking-[0.3px]">Follow-ups</h3>
              <button
                onClick={() => setShowScheduleForm(!showScheduleForm)}
                className="flex items-center gap-1 text-[11px] text-blue-600 hover:text-blue-700 font-medium"
              >
                <Plus className="w-3 h-3" />
                {showScheduleForm ? 'Cancel' : 'Schedule'}
              </button>
            </div>

            {showScheduleForm && (
              <div className="mb-3 p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Type</label>
                  <select value={scheduleForm.type} onChange={e => setScheduleForm({ ...scheduleForm, type: e.target.value })} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white">
                    <option value="Call">Call</option>
                    <option value="Meeting">Meeting</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Due date</label>
                  <input type="date" value={scheduleForm.due_date} onChange={e => setScheduleForm({ ...scheduleForm, due_date: e.target.value })} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Notes</label>
                  <textarea value={scheduleForm.notes} onChange={e => setScheduleForm({ ...scheduleForm, notes: e.target.value })} placeholder="Add notes..." className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none" rows={2} />
                </div>
                <button
                  onClick={async () => {
                    if (!scheduleForm.due_date) return showToast('Please select a due date', 'error');
                    setScheduling(true);
                    try {
                      const title = `Follow-up: ${scheduleForm.type}`;
                      await tasksAPI.create({ title, lead_id: Number(id), due_date: scheduleForm.due_date, notes: scheduleForm.notes || '', priority: 'medium' });
                      if (scheduleForm.type === 'Meeting') {
                        await communicationsAPI.logCommunication(id, { type: 'meeting', direction: 'inbound', notes: scheduleForm.notes || '', sent_at: new Date(scheduleForm.due_date).toISOString() });
                      }
                      showToast('Follow-up scheduled', 'success');
                      setShowScheduleForm(false);
                      setScheduleForm({ type: 'Call', due_date: '', notes: '' });
                      loadLeadData();
                    } catch (error) {
                      console.error('Failed to schedule follow-up:', error);
                      showToast(error.response?.data?.error || 'Failed to schedule', 'error');
                    } finally {
                      setScheduling(false);
                    }
                  }}
                  disabled={scheduling}
                  className="w-full px-3 py-1.5 bg-blue-600 text-white rounded text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
                >
                  {scheduling ? 'Scheduling...' : 'Schedule'}
                </button>
              </div>
            )}

            {followups.length === 0 ? (
              <p className="text-[12px] text-gray-500">No follow-ups scheduled</p>
            ) : (
              <div className="space-y-2">
                {[...followups]
                  .sort((a, b) => new Date(a.due_date || 0) - new Date(b.due_date || 0))
                  .map(fu => {
                    const isOverdue = fu.status !== 'completed' && fu.due_date && new Date(fu.due_date) < new Date();
                    const isDone = fu.status === 'completed';
                    const typeLabel = fu.title?.startsWith('Follow-up: ') ? fu.title.slice(10) : 'Other';
                    const borderColor = typeLabel === 'Call' ? 'border-l-blue-500' : typeLabel === 'Meeting' ? 'border-l-purple-500' : 'border-l-amber-500';
                    const typeIcon = typeLabel === 'Call' ? <Phone className="w-3 h-3 text-blue-600" /> : typeLabel === 'Meeting' ? <CalendarDays className="w-3 h-3 text-purple-600" /> : <Clock className="w-3 h-3 text-amber-600" />;
                    return (
                      <div key={fu.id} className={`border-l-[3px] ${borderColor} bg-white rounded-r-lg border border-gray-200 border-l-[3px] p-2.5 text-sm ${isDone ? 'opacity-60' : ''} ${isOverdue ? 'bg-red-50' : ''}`}>
                        <div className="flex items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              {typeIcon}
                              <p className={`font-medium text-xs ${isDone ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{typeLabel}</p>
                              {isDone && <CheckCircle size={12} className="text-green-600 flex-shrink-0" />}
                            </div>
                            {fu.due_date && (
                              <p className={`text-xs mt-0.5 flex items-center gap-1 ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                                <Calendar className="w-3 h-3" />
                                {new Date(fu.due_date).toLocaleDateString('en-GB', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                {isOverdue && <span className="text-red-600 font-semibold">(Overdue)</span>}
                              </p>
                            )}
                            {fu.notes && <p className="text-xs text-gray-500 mt-0.5 truncate">{fu.notes}</p>}
                          </div>
                          {!isDone && (
                            <button
                              onClick={async () => {
                                try {
                                  await tasksAPI.update(fu.id, { status: 'completed' });
                                  showToast('Marked as done', 'success');
                                  loadLeadData();
                                } catch (error) {
                                  console.error('Failed to complete task:', error);
                                  showToast('Failed to update', 'error');
                                }
                              }}
                              className="flex-shrink-0 text-xs text-green-600 hover:text-green-700 font-medium hover:bg-green-50 rounded px-1.5 py-0.5"
                            >
                              Mark Done
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>

          {/* Lead Health */}
          <div className="px-[18px] py-4 border-b border-gray-100">
            <h3 className="text-[10px] font-medium text-gray-400 tracking-[0.3px] mb-2">Lead health</h3>
            <div className="flex items-center justify-between px-3 py-[9px] rounded-lg" style={{ backgroundColor: '#FAEEDA' }}>
              <div className="flex items-center gap-2">
                <Flame size={14} style={{ color: '#BA7517' }} />
                <span className="text-[12px] font-medium" style={{ color: '#633806' }}>{healthInfo.label}</span>
              </div>
              {healthInfo.sub && <span className="text-[11px]" style={{ color: '#854F0B' }}>{healthInfo.sub}</span>}
            </div>
          </div>

          {/* Documents summary */}
          <div className="px-[18px] py-4 border-b border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[10px] font-medium text-gray-400 tracking-[0.3px]">Documents</h3>
              <span className="text-[11px] text-gray-500">{documents.length || 0} files</span>
            </div>
            {documents.length === 0 ? (
              <p className="text-[12px] text-gray-500 mb-2">No documents yet</p>
            ) : null}
            <label className="flex items-center gap-1.5 text-[11px] text-blue-600 hover:text-blue-700 font-medium cursor-pointer">
              <Upload size={12} />
              Upload file
              <input type="file" className="hidden" accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.webp" disabled={uploadingFile} onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setUploadingFile(true);
                setUploadError('');
                try {
                  const formData = new FormData();
                  formData.append('file', file);
                  formData.append('lead_id', id);
                  await documentsAPI.upload(formData);
                  showToast('File uploaded successfully', 'success');
                  loadDocuments();
                } catch (error) {
                  console.error('Upload failed:', error);
                  setUploadError(error.response?.data?.error || 'Failed to upload file');
                  showToast('Upload failed', 'error');
                } finally {
                  setUploadingFile(false);
                  e.target.value = '';
                }
              }} />
            </label>
          </div>
        </div>
          </div>
        </div>
      </div>

      {/* ===== MODALS ===== */}
      <DealWonModal
        isOpen={dealWonModalOpen}
        onClose={handleDealWonClose}
        onConfirm={handleDealWonConfirm}
        leadName={lead?.name || ''}
      />
      <DealLostModal
        isOpen={dealLostModalOpen}
        onClose={handleDealLostClose}
        onConfirm={handleDealLostConfirm}
        leadName={lead?.name || ''}
      />
    </div>
  );
};

export default LeadDetail;
